import { useCallback, useMemo, useState } from "react";
import { noop } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { GqlHook_Returns, StateDataObject } from "..";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { PanelBasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

// import { newSectionsData } from "../../../Assets/TestData/sectionsData";

import { LfSectionItem } from "./LocationFeedingSlices/lfStructureSlice";
import { CreatePanelLocation, CreatePanelLocationResponse, PANEL_CREATE_SECTION } from "./GQLCalls/createLfSectionCall";
import { GET_LF_SECTION_INFO, LfSectionResponse } from "./GQLCalls/getLfStructureCall";
import { START_NEXT_FEEDING_BY_LOCATION, START_NEXT_FEEDING_BY_SYSTEM, StartNextFeedingByLocationResponse, StartNextFeedingBySystemResponse } from "./GQLCalls/startNextFeedingCall";
import { FEED_LOCATION, FeedLocationResponse } from "./GQLCalls/feedLocation";

interface LfSectionsHelperFunctions {
  createSectionLocations: (domain: string, sectionName: string, panelSectionType: number, systemId: number, newSection: CreatePanelLocation[]) => void;
  startFeedingByLocation: (locationIds: number[]) => Promise<boolean>;
  startFeedingBySystem: (systemId: number) => void;
  feedLocation: (locationid: number, overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons) => Promise<boolean>;
}

interface SectionInfoInput extends PanelBasicQueryInfoInput {
  idIdentifier?: string;
}


export const useSection = (connection: Graph_QL.GQLCons): GqlHook_Returns<LfSectionItem, SectionInfoInput> & LfSectionsHelperFunctions => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const lfSectionsState = useAppSelector(({graphQlData:{domain}}) => domain.lfSections);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(lfSectionsState, Graph_QL.ToArr(connection)) as Record<string, LfSectionItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<LfSectionItem>)
  },[connection, lfSectionsState]);

  const getLfSectionInfo = useCallback(async (input: SectionInfoInput, specific?: Graph_QL.GQLCons, ) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    const {idIdentifier, ...remainingInput} = input;
    const usedInput = remainingInput ? remainingInput : {fetchAmount: 500};
    try {
      client.query<LfSectionResponse>({ query: GET_LF_SECTION_INFO, variables: { ...usedConnection, input: usedInput, idIdentifier, ifFeedCurveBaseInfoOnly: true, ifFeedCurveInfo: false, ifSpreading: false, ifSkipConsumption: false, ifSkipLastTenDaysConsumption: false }, fetchPolicy: "network-only" })// input.ids && input.ids?.length > 0 ? "network-only" : undefined}) // TODO CTO shouldn't always be network only
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.lfSections.maintained), payload: {con: usedConnection, data: e.data.section.info}});
            setCursorId(e.data.section.info.at(-1)?.section_id ?? -1)
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
        console.log(error);
      }
    },[client, connection.connection, dispatch])

  const createSectionLocations = async (domain: string, sectionName: string, panelSectionType: number, systemId: number, newSection: CreatePanelLocation[]) => {
    try {
      client.mutate<CreatePanelLocationResponse>({mutation: PANEL_CREATE_SECTION, variables: {domain, sectionName, panelSectionType, systemId, section: newSection}})
        .then((result) => {if (result.data?.section.create.success) {getLfSectionInfo({ids: result.data.section.create.updatedValues, idIdentifier: "section_id"}, connection)}}).catch((error) => {console.log(error)});
    } catch (error) {
      console.log(error);
    }
  }

  const startFeedingByLocation = async (locations: number[]) => {
    try {
      return client.mutate<StartNextFeedingByLocationResponse>({mutation: START_NEXT_FEEDING_BY_LOCATION, variables: {...connection.connection, locationId: locations}})
        .then((result) => {
          console.log(result.data?.location.startNextFeeding)
          return true
        }).catch(() => false);
    } catch (error) {
      console.log(error);
      return false
    }
  }

  const startFeedingBySystem = async (systemId: number) => {
    try {
      client.mutate<StartNextFeedingBySystemResponse>({mutation: START_NEXT_FEEDING_BY_SYSTEM, variables: {...connection.connection, feedCurveId: 0, systemId}})
        .then((result) => {
          console.log(result);
        })
    } catch (error) {
      console.log(error);
    }
  }

    const feedLocation = useCallback(async (locationId: number, overwriteFeedcurve: boolean, amount: number, specific?: Graph_QL.GQLCons) => {
      const usedConnection = specific ? specific.connection : connection.connection 
      try {
        return client.mutate<FeedLocationResponse>({mutation: FEED_LOCATION, variables: {...usedConnection, locationid: locationId, overwriteFeedcurve, amount}})
        .then((e) => {
          const feedLocation = e.data?.location.feed;
          if (feedLocation) {
            return feedLocation.success? true:false;
          }
          return false;
        }).catch((error) => {return false})
      } catch (error) {console.log(error); return false}
    },[client, connection.connection])
    

  return {call: getLfSectionInfo, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries: noop, createSectionLocations, startFeedingByLocation, startFeedingBySystem, feedLocation}
} 

